import { API_URL } from './config';

interface ApiResponse<T = any> {
  data: T;
}

export const createApiClient = (authenticatedFetch: (url: string, options?: any) => Promise<Response | null>) => {
  const request = async <T = any>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> => {
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    
    // Pass options directly - authenticatedFetch will handle headers
    const response = await authenticatedFetch(fullUrl, options);
    
    if (!response) {
      throw new Error('Failed to get response');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.error || errorData.message || response.statusText);
      error.response = {
        status: response.status,
        data: errorData
      };
      throw error;
    }

    const data = await response.json();
    return { data };
  };

  return {
    get: <T = any>(url: string, params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<T>(url + queryString, { method: 'GET' });
    },
    
    post: <T = any>(url: string, data?: any, config?: RequestInit) => {
      const isFormData = data instanceof FormData;
      return request<T>(url, {
        method: 'POST',
        body: isFormData ? data : JSON.stringify(data),
        ...config,
      });
    },
    
    put: <T = any>(url: string, data?: any) => {
      return request<T>(url, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    
    delete: <T = any>(url: string) => {
      return request<T>(url, { method: 'DELETE' });
    },
  };
};